import { Check, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { useTicker } from '../api'
import { seatStyle } from '../helpers'
import type { BoardProps } from './types'

interface TriviaView {
  i: number
  total: number
  phase: 'ask' | 'reveal' | 'done'
  question: string
  options: string[]
  deadline: number
  revealUntil: number
  correct: number | null
  mine: number | null
  locked: boolean[]
  choices: (number | null)[] | null
  scores: number[]
  gain: number[] | null
}

export const ASK_MS = 15_000

export function Trivia({ view, seat, over, busy, send, skew, players, people }: BoardProps<TriviaView>) {
  const now = useTicker(200) + skew
  const name = (s: number) => (s === seat ? 'You' : (people.get(players[s] ?? '')?.displayName.split(' ')[0] ?? 'Player'))
  const asking = view.phase === 'ask' && !over
  const left = Math.max(0, view.deadline - now)
  const answered = seat !== null && view.locked[seat]
  const reveal = view.phase !== 'ask'
  const order = view.scores.map((sc, s) => ({ sc, s })).sort((a, b) => b.sc - a.sc)
  return (
    <div className="mx-auto grid w-full max-w-xl gap-4">
      <div className="flex items-center justify-between text-xs text-text-muted">
        <span>
          Question {Math.min(view.i + 1, view.total)} of {view.total}
        </span>
        {asking ? <span className="num tabular-nums">{Math.ceil(left / 1000)}s</span> : reveal && !over ? <span>Next question soon…</span> : null}
      </div>
      <div className="h-1.5 overflow-hidden rounded-full bg-muted" aria-hidden>
        <div className={cn('h-full transition-[width] duration-200 ease-linear', left < 5000 ? 'bg-destructive' : 'bg-brand')} style={{ width: asking ? `${(left / ASK_MS) * 100}%` : '0%' }} />
      </div>
      <h3 className="text-center text-lg font-semibold leading-snug">{view.question}</h3>
      <div className="grid gap-2 sm:grid-cols-2">
        {view.options.map((o, i) => {
          const right = reveal && view.correct === i
          const picks = view.choices ? view.choices.flatMap((c, s) => (c === i ? [s] : [])) : []
          const wrongPick = reveal && view.correct !== i && picks.length > 0
          return (
            <Button
              key={i}
              type="button"
              variant="outline"
              disabled={!asking || seat === null || answered || busy}
              onClick={() => send({ choice: i })}
              className={cn('h-auto min-h-12 justify-start whitespace-normal py-2 text-left', view.mine === i && asking && 'border-brand bg-brand-soft', right && 'border-success bg-success-soft', wrongPick && 'border-destructive/40 bg-destructive/10')}
            >
              <span className="grid size-6 shrink-0 place-items-center rounded-full bg-muted text-xs font-semibold">{'ABCD'[i]}</span>
              <span className="flex-1">{o}</span>
              {right ? <Check className="size-4 text-success" aria-label="Correct" /> : wrongPick ? <X className="size-4 text-destructive" aria-hidden /> : null}
              {picks.length ? (
                <span className="flex -space-x-1">
                  {picks.map((s) => (
                    <span key={s} className={cn('size-3 rounded-full border border-background', seatStyle(s).bg)} title={name(s)} />
                  ))}
                </span>
              ) : null}
            </Button>
          )
        })}
      </div>
      <p className="min-h-5 text-center text-sm text-text-secondary" aria-live="polite">
        {asking ? (seat === null ? 'Watching: answers are hidden until time is up.' : answered ? 'Locked in. Waiting for the others…' : 'Pick fast: quicker right answers score more.') : ''}
      </p>
      <ol className="grid gap-1" aria-label="Scores">
        {order.map(({ sc, s }, rank) => (
          <li key={s} className="flex items-center gap-2 rounded-md bg-muted px-3 py-1 text-sm">
            <span className="num w-4 text-xs text-text-muted">{rank + 1}</span>
            <span className={cn('size-2.5 rounded-full', seatStyle(s).bg)} />
            <span className="flex-1 font-medium">{name(s)}</span>
            {asking ? <span className={cn('text-xs', view.locked[s] ? 'text-success' : 'text-text-muted')}>{view.locked[s] ? 'answered' : 'thinking'}</span> : null}
            {view.gain && view.gain[s] ? <span className="num text-xs font-medium text-success tabular-nums">+{view.gain[s]}</span> : null}
            <span className="num w-12 text-right font-semibold tabular-nums">{sc}</span>
          </li>
        ))}
      </ol>
    </div>
  )
}
